import { useState } from 'react'
import { Plus } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import type { ComponentConfigV2, ComponentTypeV2 } from '@/types/configurationV2'
import {
  COMPONENT_TYPE_INFO,
  createDefaultQuizOptions,
  createDefaultVideoOptions,
  createDefaultWarmupOptions,
  createDefaultBuildupOptions,
} from '@/types/configurationV2'
import { buildComponent } from './componentConfig'

interface ComponentAdderProps {
  onAdd: (component: ComponentConfigV2) => void
  existingCount: number
  allowedTypes?: ComponentTypeV2[]
}

const DEFAULT_OPTIONS: Record<ComponentTypeV2, () => ComponentConfigV2['options']> = {
  quiz: createDefaultQuizOptions,
  video: createDefaultVideoOptions,
  toon: () => ({ toonType: 'intro' as const }),
  assignment: () => ({ diaryQuizSetId: undefined }),
  interactive: () => ({ kind: 'tool' }),
  guide: () => ({ templateId: null, showMessage: false, audioResponse: { enabled: false, required: false } }),
  buildup: createDefaultBuildupOptions,
  warmup: createDefaultWarmupOptions,
}

export default function ComponentAdder({
  onAdd,
  existingCount,
  allowedTypes = ['quiz', 'video', 'toon', 'assignment', 'interactive', 'guide', 'buildup', 'warmup'],
}: ComponentAdderProps) {
  const [showMenu, setShowMenu] = useState(false)

  const handleAdd = (type: ComponentTypeV2) => {
    const component = buildComponent(type, existingCount, uuidv4(), DEFAULT_OPTIONS[type]())
    onAdd(component)
    setShowMenu(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-center text-gray-400 text-sm hover:border-purple-400 hover:text-purple-500 transition-colors"
      >
        <Plus className="w-4 h-4 inline mr-1" />
        컴포넌트 추가
      </button>

      {showMenu && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-2">
            {allowedTypes.map((type) => {
              const info = COMPONENT_TYPE_INFO[type]
              return (
                <button
                  key={type}
                  onClick={() => handleAdd(type)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 rounded-md"
                >
                  <span
                    className="w-6 h-6 rounded flex items-center justify-center text-white text-xs"
                    style={{ backgroundColor: info.color }}
                  >
                    {info.icon}
                  </span>
                  {info.label}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
